import { useEffect, useState } from "react";
import { Alert, Button, Card, Stack, formatMoneyMinor } from "@leylion/design-system";
import { previewCashierRefund } from "../api/client";
import type { RefundPreview, RefundTender } from "../api/cashier-types";
import { valueError, valueLabel } from "../shared/value-copy";

export function useRefundPreview(orderId: string | undefined, orderVersion: number | undefined,
  lines: Array<{ originalOrderLineId: string; quantityMilli: number }>, enabled: boolean) {
  const [revision, setRevision] = useState(0);
  const key = enabled && orderId && lines.length ? JSON.stringify([orderId, orderVersion, lines, revision]) : "";
  const [state, setState] = useState<{ key: string; preview?: RefundPreview; error?: unknown }>({ key: "" });
  useEffect(() => {
    let active = true;
    if (!key) { setState({ key: "" }); return; }
    const timer = window.setTimeout(() => {
      void previewCashierRefund(orderId!, lines).then(preview => {
        if (!preview?.previewToken || preview.orderId !== orderId || !Array.isArray(preview.paymentAllocations))
          throw new Error("Refund preview could not be verified.");
        if (active) setState({ key, preview });
      }).catch(error => { if (active) setState({ key, error }); });
    }, 150);
    return () => { active = false; window.clearTimeout(timer); };
  }, [key]);
  return {
    preview: key && state.key === key ? state.preview : undefined,
    error: key && state.key === key ? state.error : undefined,
    loading: Boolean(key && (state.key !== key || (!state.preview && !state.error))),
    refresh: () => { setState({ key: "" }); setRevision(value => value + 1); }
  };
}

export function RefundTenderDetails({ rows, currency, language }: { rows: RefundTender[]; currency: string; language: "ar" | "en" }) {
  const ar = language === "ar";
  const money = (v: number) => formatMoneyMinor(v, currency, ar ? "ar-AE" : "en-AE");
  return <div>{rows.map((row, index) => <div key={index} data-testid="refund-tender-row">
    <strong>{row.method === "Account" ? (ar ? "حساب العميل" : "Customer account") : valueLabel(row.method, language)}: {money(row.amountMinor)}</strong>
    {row.originalGiftCardNumber ? <div>{ar ? "البطاقة الأصلية" : "Original card"}: <bdi>{row.originalGiftCardNumber}</bdi></div> : null}
    {row.returnGiftCardNumber ? <div>{ar ? "البطاقة المستلمة للاسترداد" : "Refund destination card"}: <bdi>{row.returnGiftCardNumber}</bdi></div> : null}
    {row.recipientName ? <div>{ar ? "المستلم" : "Recipient"}: {row.recipientName}</div> : null}
    {row.replacementRequired ? <div>{ar ? "الأصلية منتهية؛ يتطلب هذا الجزء بطاقة بديلة لإتمام الاسترداد." : "The original card expired; this portion requires a replacement to complete the refund."}</div> : null}
  </div>)}</div>;
}

export function RefundAllocationPreview({ preview, loading, error, retry, language }: {
  preview?: RefundPreview; loading: boolean; error?: unknown; retry: () => void; language: "ar" | "en";
}) {
  const ar = language === "ar";
  if (loading) return <p role="status">{ar ? "جارٍ حساب توزيع الاسترداد…" : "Calculating refund allocation…"}</p>;
  if (error) return <Stack><Alert tone="danger" title={ar ? "تعذرت معاينة الاسترداد" : "Refund preview failed"}>{valueError(error, language)}</Alert>
    <Button variant="secondary" onClick={retry}>{ar ? "تحديث المعاينة" : "Refresh preview"}</Button></Stack>;
  if (!preview) return null;
  const money = (v: number) => formatMoneyMinor(v, preview.currency, ar ? "ar-AE" : "en-AE");
  const cash = preview.paymentAllocations.filter(x => x.method === "Cash").reduce((sum, x) => sum + x.amountMinor, 0);
  return <Stack><Card title={ar ? "راجع توزيع الاسترداد قبل التأكيد" : "Review the refund allocation before confirming"}>
    <div data-testid="refund-allocation-preview">
      <p><strong>{ar ? "إجمالي الاسترداد" : "Total refund"}: {money(preview.totalMinor)}</strong></p>
      <p>{ar ? "النقد الذي سيُسلّم للعميل" : "Cash to hand to the customer"}: {money(cash)}</p>
      <RefundTenderDetails rows={preview.paymentAllocations} currency={preview.currency} language={language} />
    </div>
  </Card></Stack>;
}
